import PropTypes from 'prop-types';

import { appPropTypes } from '@techchange/components';

import { RESOURCE_TYPES } from '../../../constants';

import { RESOURCE_VIDEO_PROVIDERS } from './constants';

/**
 * Expected object shape for an image resource's
 * "data" field.
 */
export const imageDataShape = {

	/**
	 * The content source URL.
	 */
	src: PropTypes.string,

	/**
	 * Alternative text for the image.
	 */
	alt: PropTypes.string,

	/**
	 * The image link.
	 */
	href: PropTypes.string,

	/**
	 * The image link target.
	 */
	target: PropTypes.oneOf(['_blank', '_self']),

};

/**
 * Expected object shape for a video resource's
 * "data" field.
 */
export const videoDataShape = {

	/**
	 * The video provider to determine the video source.
	 */
	provider: PropTypes.oneOf([
		RESOURCE_VIDEO_PROVIDERS.LIBRARY,
		RESOURCE_VIDEO_PROVIDERS.URL,
	]),

	/**
	 * The video source URL for a provider of "URL".
	 */
	src: PropTypes.string,

	/**
	 * The video ID for a provider of "Library".
	 */
	video_id: PropTypes.string,

	/**
	 * The video aspect ratio.
	 */
	aspectRatio: PropTypes.string,

};

/**
 * Expected object shape for an embed resource's
 * "data" field.
 */
export const embedDataShape = {

	/**
	 * True, if the embed is for an articulate project.
	 */
	articulate: PropTypes.bool,

	/**
	 * The embed aspect ratio (if any).
	 */
	aspectRatio: PropTypes.string,

	/**
	 * The embed url.
	 */
	src: PropTypes.string,

};

/**
 * Expected object shape for an Miro resource's
 * "data" field.
 */
export const miroDataShape = {

	/**
	 * Unique identifier of the associated Miro team.
	 */
	team_id: PropTypes.string,

	/**
	 * Unique identifier of the associated Miro board.
	 */
	board_id: PropTypes.string,

};

/**
 * Prop type for the Resource "data" field. Note that this
 * includes all possible attributes across all resource types,
 * but each resource type will have differing expected attributes.
 */
const dataPropType = PropTypes.shape({

	/**
	 * Unique identifier of the link resource ("link"
	 * resource types only).
	 */
	id: PropTypes.string,

	/**
	 * Unique identifier of the link resource ("link"
	 * resource types only).
	 */
	type: PropTypes.string,

	/**
	 * The link title attribute ("link" resource types only).
	 */
	title: PropTypes.string,

	/**
	 * The link "rel" attribute ("link" resource types only).
	 */
	rel: PropTypes.string,

	/**
	 * Flag indicating that link is present in all existing translations
	 * ("link" resource types only).
	 */
	link_sync: PropTypes.string,

	/**
	 * Alternative text for the content
	 * ("image" resource types only).
	 */
	alt: PropTypes.string,

	/**
	 * The content link ("image" and "link"
	 * resource types only).
	 */
	href: PropTypes.string,

	/**
	 * The content link target ("image" and "link"
	 * resource types only).
	 */
	target: PropTypes.oneOf(['_blank', '_self']),

	/**
	 * The content source URL ("image", "video", and
	 * "embed" resource types only).
	 */
	src: PropTypes.string,

	/**
	 * The content aspect ratio ("video" and
	 * "embed" resource types only).
	 */
	aspectRatio: PropTypes.string,

	/**
	 * Flag indicating that source URL is an Articulate file
	 * ("embed" resource types only).
	 */
	articulate: PropTypes.bool,

	/**
	 * Unique identifier of the associated form ("form",
	 * "poll", and "quiz" resource types only).
	 */
	form_id: PropTypes.string,

	/**
	 * Unique identifier of the associated Miro team ("miro",
	 * resource types only).
	 */
	team_id: PropTypes.string,

	/**
	 * Unique identifier of the associated Miro board ("miro",
	 * resource types only).
	 */
	board_id: PropTypes.string,

});

/**
 * Expected object shape for the ResourceContent model.
 */
const resourceContentShape = {

	/**
	 * The ResourceContent ID.
	 */
	id: PropTypes.string,

	/**
	 * The content language.
	 */
	language: PropTypes.string.isRequired,

	/**
	 * The content title.
	 */
	title: PropTypes.string,

	/**
	 * Additional data for the content.
	 */
	data: dataPropType,

	/**
	 * The Prosemirror content.
	 */
	content: appPropTypes.proseMirrorDocument,

	/**
	 * The scope of the content,
	 * i.e. "session", "course", "organization", etc.
	 */
	scope: PropTypes.string,

	/**
	 * The scope ID of the content.
	 */
	scope_id: PropTypes.string,

	/**
	 * The content organization ID.
	 */
	organization_id: PropTypes.string,

	/**
	 * The timestamp of content creation (if any).
	 */
	created_at: PropTypes.string,

	/**
	 * The timestamp of content update (if any).
	 */
	updated_at: PropTypes.string,

};

/**
 * Expected object shape for the Resource model.
 */
const resourceShape = {

	/**
	 * The unique UUID for the content.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The type of the content.
	 */
	type: PropTypes.oneOf(Object.values(RESOURCE_TYPES)).isRequired,

	/**
	 * The width of the content.
	 */
	width: PropTypes.number.isRequired,

	/**
	 * The title of the content.
	 */
	title: PropTypes.string,

	/**
	 * Flag indicating if the content title should be visible.
	 */
	show_title: PropTypes.bool,

	/**
	 * Slug for the resource
	 */
	slug: PropTypes.string,

	/**
	 * The date the resource opens on.
	 */
	open_date: PropTypes.string,

	/**
	 * The resource URL.
	 */
	url: PropTypes.string,

	/**
	 * Creator id for the resource.
	 */
	creator_id: PropTypes.string,

	/**
	 * The Prosemirror content.
	 */
	content: appPropTypes.proseMirrorDocument,

	/**
	 * Additional data for the content.
	 */
	data: dataPropType,

	/**
	 * The list of loaded resource contents.
	 */
	resource_contents: PropTypes.arrayOf(PropTypes.shape(resourceContentShape)),

	/**
	 * The children resources (if any).
	 */
	children: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The child resource ID.
		 */
		id: PropTypes.string,

	})),

	/**
	 * The scope of the content,
	 * i.e. "session", "course", "organization", etc.
	 */
	scope: PropTypes.string,

	/**
	 * The scope ID of the content.
	 */
	scope_id: PropTypes.string,

	/**
	 * The content organization ID.
	 */
	organization_id: PropTypes.string,

	/**
	 * The timestamp of content creation (if any).
	 */
	created_at: PropTypes.string,

	/**
	 * The timestamp of content update (if any).
	 */
	updated_at: PropTypes.string,

};

/**
 * Expected object shape for the initialValues prop passed into
 * resource ContentEditor components. Note that certain resource
 * types might have additional expected properties as documented
 * in the component file directly.
 */
export const initialValuesShape = {

	/**
	 * The title of the content.
	 */
	title: PropTypes.string,

	/**
	 * The currently selected language for the editor.
	 */
	language: PropTypes.string,

	/**
	 * The currently selected content width.
	 */
	width: PropTypes.number,

	/**
	 * The list of loaded resource contents available to edit.
	 */
	resourceContents: PropTypes.arrayOf(PropTypes.shape(resourceContentShape)),

	/**
	 * The list of resource contents set to be deleted on form submission.
	 */
	deletedResourceContents: PropTypes.arrayOf(PropTypes.shape(resourceContentShape)),

};

export const contentBuilderPropTypes = {

	resourceContent: PropTypes.shape(resourceContentShape),

	resource: PropTypes.shape(resourceShape),

	/**
	 * Prop types for the resource "content" components.
	 */
	content: {

		data: PropTypes.shape({

			...resourceShape,

			/**
			 * The selected language for the resource in the current editor state.
			 */
			language: PropTypes.string,

			/**
			 * The list of resource contents to delete in the current editor state.
			 */
			deleted_resource_contents: PropTypes.arrayOf(PropTypes.shape(resourceContentShape)),

		}).isRequired,

		/**
		 * The translated content for the resource in the final language to render.
		 */
		resourceContent: PropTypes.shape(resourceContentShape),

		/**
		 * The current user's ID.
		 */
		userId: PropTypes.string,

		/**
		 * Flag indicating if the current user is an admin.
		 */
		isAdmin: PropTypes.bool,

		/**
		 * Flag indicating if the content is being edited.
		 */
		isEditing: PropTypes.bool,

		/**
		 * Admins Only: API integrations data for the current user.
		 */
		apiIntegrations: appPropTypes.apiIntegrations,

		/**
		 * The settings for filestack to handle image uploads and rendering.
		 */
		filestack: appPropTypes.filestack.isRequired,

		/**
		 * The users related to the scope of this content,
		 * (i.e. all enrolled users in a session).
		 *
		 * NOTE: Map from user ID -> user data.
		 */
		users: appPropTypes.allUsers,

		/**
		 * The TechChange socket instance to handle real-time events.
		 */
		socket: appPropTypes.socket,

		/**
		 * A list of callback functions to trigger based on events in the content.
		 */
		triggers: PropTypes.shape({

			/**
			 * Callback function to handle clicking a link.
			 */
			onLinkClick: PropTypes.func.isRequired,

			/**
			 * Callback function to handle clicking on a user.
			 */
			onUserClick: PropTypes.func.isRequired,

			/**
			 * Callback function to handle watching a video.
			 */
			onVideoWatch: PropTypes.func.isRequired,

			/**
			 * Admins Only: Callback function to handle changing the language.
			 */
			onLanguageChange: PropTypes.func,

			/**
			 * Admins Only: Callback function to handle changing the width.
			 */
			onWidthChange: PropTypes.func,

		}).isRequired,

		/**
		 * Callback function to handle using an API integration.
		 */
		onUseApiIntegration: PropTypes.func,

		/**
		 * Callback function to handle the content editor being cancelled.
		 */
		onCancel: PropTypes.func.isRequired,

		/**
		 * Callback function to handle saving the content editor.
		 */
		onSave: PropTypes.func.isRequired,

	},

	/**
	 * Prop types for the resource "ContentEditor" components.
	 */
	contentEditor: {

		/**
		 * The unique ID for the content.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The type of content.
		 */
		type: PropTypes.oneOf(Object.values(RESOURCE_TYPES)).isRequired,

		/**
		 * The initial values of the form.
		 */
		initialValues: PropTypes.shape(initialValuesShape).isRequired,

		/**
		 * Flag indicating if a new slide is being created.
		 */
		isNew: PropTypes.bool,

		/**
		 * Callback function to handle using an API integration.
		 */
		onUseApiIntegration: PropTypes.func,

		/**
		 * Function to handle saving the content.
		 */
		onSave: PropTypes.func.isRequired,

		/**
		 * Function to handle cancel of editing the content.
		 */
		onCancel: PropTypes.func.isRequired,

		/**
		 * Callback function to handle changing the language.
		 */
		onLanguageChange: PropTypes.func.isRequired,

		/**
		 * Callback function to handle changing the width.
		 */
		onWidthChange: PropTypes.func.isRequired,

	},

};
